import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Plus, LogOut, Trash2, Pause, Play, Eye, Copy, Image as ImageIcon, Upload, RotateCcw, Gift, X, Tag, Link as LinkIcon, DollarSign } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { invalidateBrandingCache } from "../lib/branding";
import { useConfirm } from "../components/ConfirmModal";

export default function SuperadminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newCreds, setNewCreds] = useState(null);
  const [showBranding, setShowBranding] = useState(false);
  const [showPromos, setShowPromos] = useState(false);
  const [billingMap, setBillingMap] = useState({});
  const confirm = useConfirm();

  const load = async () => {
    try {
      const [m, s, t, b] = await Promise.all([
        api.get("/superadmin/me"),
        api.get("/superadmin/stats"),
        api.get("/superadmin/tenants"),
        api.get("/superadmin/billing").catch(() => ({ data: [] })),
      ]);
      setMe(m.data); setStats(s.data); setTenants(t.data);
      const map = {};
      (b.data || []).forEach((row) => { map[row.id] = row; });
      setBillingMap(map);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("pragma_super_token");
        navigate("/superadmin/login");
      } else { toast.error("Failed to load"); }
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("pragma_super_token")) {
      navigate("/superadmin/login");
      return;
    }
    load();
    /* eslint-disable-next-line */
  }, []);

  if (!localStorage.getItem("pragma_super_token")) {
    return <Navigate to="/superadmin/login" replace />;
  }

  const logout = () => {
    localStorage.removeItem("pragma_super_token");
    navigate("/superadmin/login");
  };

  const toggleStatus = async (t) => {
    const next = t.status === "active" ? "suspended" : "active";
    try {
      await api.patch(`/superadmin/tenants/${t.id}`, { status: next });
      toast.success(next === "active" ? "Reactivated" : "Suspended");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (t) => {
    const ok = await confirm({
      title: `Delete "${t.name}"?`,
      message: `This permanently DELETES the tenant "${t.name}" and ALL its data (users, leads, deals, files). This cannot be undone.`,
      destructive: true, confirmLabel: "I understand — continue",
    });
    if (!ok) return;
    const typed = await confirm({
      title: "Type the slug to confirm",
      message: `Type the tenant slug "${t.slug}" exactly to confirm deletion.`,
      input: { placeholder: t.slug, required: true, label: "Slug" },
      destructive: true, confirmLabel: "Delete tenant forever",
    });
    if (typed !== t.slug) { toast.error("Slug did not match"); return; }
    try {
      await api.delete(`/superadmin/tenants/${t.id}`);
      toast.success("Tenant deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const impersonate = async (t) => {
    try {
      const { data } = await api.post(`/superadmin/tenants/${t.id}/impersonate`);
      localStorage.setItem("pragma_token", data.token);
      toast.success(`Impersonating ${t.name}`);
      window.location.href = "/";
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const extendTrial = async (t) => {
    const daysStr = await confirm({
      title: `Extend ${t.name}'s trial`,
      message: "By how many days should the trial be extended?",
      input: { type: "number", placeholder: "7", defaultValue: "7", required: true, label: "Days" },
      confirmLabel: "Extend trial",
    });
    if (!daysStr) return;
    const days = parseInt(daysStr, 10);
    if (!days || days <= 0) return;
    try {
      await api.post(`/superadmin/billing/${t.id}/extend-trial`, { days });
      toast.success(`Trial extended by ${days} day${days === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const seedDemo = async () => {
    const ok = await confirm({
      title: "Re-seed the XYZco demo tenant?",
      message: "Any existing XYZco data will be replaced with fresh fixture data. Other tenants are not affected.",
      destructive: true, confirmLabel: "Wipe & re-seed",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/superadmin/seed-demo`);
      const s = data.summary;
      toast.success(
        `XYZco seeded · ${s.companies} cos · ${s.contacts} contacts · ${s.deals} deals.\n` +
        `Login: demo@xyzco.com / Demo2026!`,
        { duration: 8000 }
      );
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Seed failed"); }
  };

  const compUpgrade = async (t) => {
    const choice = await confirm({
      title: `Comp upgrade ${t.name}?`,
      message: "Comp this tenant to a free paid account. Enter duration in months (e.g. 12) or leave blank for indefinite.",
      input: { type: "number", placeholder: "12", defaultValue: "12", required: false, label: "Months (blank = indefinite)" },
      confirmLabel: "Continue",
    });
    if (choice === null) return;
    const months = choice.trim() === "" ? null : parseInt(choice, 10);
    if (months !== null && (!Number.isFinite(months) || months <= 0)) {
      toast.error("Enter a positive number of months, or leave blank for indefinite.");
      return;
    }
    const reason = await confirm({
      title: "Reason for comp (optional)",
      input: { placeholder: "Comped by Pragma", defaultValue: "Comped by Pragma", required: false, label: "Reason" },
      confirmLabel: months ? `Comp for ${months} month${months === 1 ? "" : "s"}` : "Comp indefinitely",
    });
    if (reason === null) return;
    try {
      await api.post(`/superadmin/billing/${t.id}/comp-upgrade`, {
        duration_months: months, reason,
      });
      toast.success(
        months ? `${t.name} comped for ${months} month${months === 1 ? "" : "s"}`
               : `${t.name} comped indefinitely`
      );
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const compRevoke = async (t) => {
    if (!await confirm({
      title: `Revoke comp upgrade?`,
      message: `${t.name} will return to their trial/Stripe state.`,
      destructive: true, confirmLabel: "Revoke comp",
    })) return;
    try {
      await api.post(`/superadmin/billing/${t.id}/comp-revoke`);
      toast.success(`Comp revoked for ${t.name}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--pg-bg)" }} data-testid="superadmin-dashboard">
      <div className="px-8 py-5 flex items-center justify-between" style={{ borderBottom: "1px solid var(--pg-border)" }}>
        <div className="flex items-center gap-3">
          <span className="pg-badge pg-badge-purple">SUPERADMIN</span>
          <h1 className="text-xl" style={{ fontWeight: 600 }}>Pragma Operations</h1>
          {me && <span className="mono text-[11px] text-mute">· {me.email}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button className="pg-btn" onClick={() => setShowBranding(true)} data-testid="branding-btn">
            <ImageIcon size={14} /> Branding
          </button>
          <button className="pg-btn" onClick={() => setShowPromos(true)} data-testid="promos-btn">
            <Tag size={14} /> Promo Codes
          </button>
          <button className="pg-btn" onClick={seedDemo} data-testid="seed-demo-btn" title="Wipe & re-seed XYZco demo tenant"><Gift size={14} /> Seed XYZco Demo</button>
          <button className="pg-btn pg-btn-primary" onClick={() => setShowNew(true)} data-testid="new-tenant-btn"><Plus size={14} /> New Tenant</button>
          <button className="pg-btn pg-btn-ghost" onClick={logout} data-testid="superadmin-logout"><LogOut size={14} /> Sign out</button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-3 gap-4 p-8">
          <Stat label="Active Tenants" value={stats.active_tenants} testid="stat-active" />
          <Stat label="Suspended" value={stats.suspended_tenants} testid="stat-suspended" />
          <Stat label="Total Users" value={stats.user_count} testid="stat-users" />
        </div>
      )}

      <div className="px-8 pb-8">
        <h2 className="text-sm mono uppercase tracking-wider text-mute mb-3">All Tenants</h2>
        {tenants.length === 0 ? (
          <div className="empty-state">No tenants yet. Create the first workspace.</div>
        ) : (
          <div className="pg-card overflow-hidden">
            <table className="pg-table">
              <thead>
                <tr><th>Name</th><th>Slug</th><th>Plan</th><th>Status</th><th>Billing</th><th>Users</th><th>Created</th><th>Last Login</th><th></th></tr>
              </thead>
              <tbody>
                {tenants.map(t => {
                  const b = billingMap[t.id];
                  let billingLabel = "—";
                  let billingClass = "pg-badge";
                  if (b) {
                    if (b.comped) {
                      const until = b.comped_until ? new Date(b.comped_until) : null;
                      billingLabel = until
                        ? `Comped · until ${until.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
                        : "Comped · indefinite";
                      billingClass = "pg-badge pg-badge-lime";
                    }
                    else if (b.subscription_status === "active") { billingLabel = "Active"; billingClass = "pg-badge pg-badge-teal"; }
                    else if (b.subscription_status === "trialing") { billingLabel = "Trialing"; billingClass = "pg-badge pg-badge-purple"; }
                    else if (b.subscription_status === "past_due") { billingLabel = "Past due"; billingClass = "pg-badge pg-badge-red"; }
                    else if (b.subscription_status === "canceled") { billingLabel = "Canceled"; billingClass = "pg-badge"; }
                    else if (b.in_trial) {
                      const end = b.trial_end ? new Date(b.trial_end) : null;
                      const days = end ? Math.max(0, Math.ceil((end - Date.now()) / 86400000)) : 0;
                      billingLabel = `Trial · ${days}d`; billingClass = "pg-badge pg-badge-purple";
                    } else { billingLabel = "Expired"; billingClass = "pg-badge pg-badge-red"; }
                  }
                  return (
                  <tr key={t.id} data-testid={`tenant-row-${t.id}`}>
                    <td style={{ fontWeight: 600, color: "var(--pg-text)" }}>{t.name}</td>
                    <td className="mono text-xs">{t.slug}</td>
                    <td className="mono text-xs">{t.plan}</td>
                    <td>{t.status === "active"
                        ? <span className="pg-badge pg-badge-teal">active</span>
                        : <span className="pg-badge pg-badge-red">suspended</span>}</td>
                    <td><span className={billingClass} data-testid={`billing-status-${t.id}`}>{billingLabel}</span></td>
                    <td className="mono">{t.user_count}</td>
                    <td className="mono text-xs">{(t.created_at || "").slice(0, 10)}</td>
                    <td className="mono text-xs">{(t.last_login_at || "").slice(0, 16) || "—"}</td>
                    <td>
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => extendTrial(t)} className="pg-btn" style={{ padding: "0.3rem 0.6rem", fontSize: 11 }} data-testid={`extend-trial-${t.id}`} title="Extend trial">+Trial</button>
                        {b?.comped ? (
                          <button onClick={() => compRevoke(t)} className="pg-btn" style={{ padding: "0.3rem 0.6rem", fontSize: 11 }} data-testid={`comp-revoke-${t.id}`} title="Revoke comp"><X size={12} /> Revoke</button>
                        ) : (
                          <button onClick={() => compUpgrade(t)} className="pg-btn" style={{ padding: "0.3rem 0.6rem", fontSize: 11 }} data-testid={`comp-upgrade-${t.id}`} title="Comp upgrade — free paid account"><Gift size={12} /> Comp</button>
                        )}
                        <button onClick={() => impersonate(t)} className="pg-btn" style={{ padding: "0.3rem 0.6rem", fontSize: 11 }} data-testid={`impersonate-${t.id}`} title="View as Admin"><Eye size={12} /></button>
                        <button onClick={() => toggleStatus(t)} className="pg-btn" style={{ padding: "0.3rem 0.6rem" }} title={t.status === "active" ? "Suspend" : "Reactivate"} data-testid={`toggle-${t.id}`}>
                          {t.status === "active" ? <Pause size={12} /> : <Play size={12} />}
                        </button>
                        <button onClick={() => remove(t)} className="pg-btn pg-btn-danger" style={{ padding: "0.3rem 0.6rem" }} data-testid={`delete-${t.id}`}><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showNew && <NewTenantPanel onClose={() => setShowNew(false)} onCreated={(creds) => { setShowNew(false); setNewCreds(creds); load(); }} />}
      {newCreds && <CredentialsPanel creds={newCreds} onClose={() => setNewCreds(null)} />}
      {showBranding && <BrandingPanel onClose={() => setShowBranding(false)} />}
      {showPromos && <PromoCodesPanel onClose={() => setShowPromos(false)} />}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="metric-card" data-testid={testid}>
      <div className="text-xs uppercase" style={{ color: "var(--pg-text-mute)", letterSpacing: "0.08em" }}>{label}</div>
      <div className="text-4xl mt-3" style={{ fontWeight: 800, letterSpacing: "-0.035em" }}>{value}</div>
    </div>
  );
}

function NewTenantPanel({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", slug: "", admin_email: "", admin_first_name: "", admin_last_name: "", plan: "free" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/superadmin/tenants", form);
      onCreated({ tenant: data.tenant, admin: data.admin });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} />
      <div className="side-panel" data-variant="center" data-testid="new-tenant-panel">
        <div className="px-6 py-5" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <h2 className="text-xl">New Tenant</h2>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <Field label="Company Name" value={form.name} onChange={v => setForm({ ...form, name: v, slug: form.slug || v.toLowerCase().replace(/[^a-z0-9]+/g, "-") })} testid="tenant-name" required />
          <Field label="Workspace Slug" value={form.slug} onChange={v => setForm({ ...form, slug: v.toLowerCase().replace(/[^a-z0-9-]+/g, "-") })} testid="tenant-slug" required hint="lowercase, hyphens allowed" />
          <Field label="Admin Email" type="email" value={form.admin_email} onChange={v => setForm({ ...form, admin_email: v })} testid="tenant-admin-email" required />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Admin First Name" value={form.admin_first_name} onChange={v => setForm({ ...form, admin_first_name: v })} testid="tenant-admin-fname" required />
            <Field label="Admin Last Name" value={form.admin_last_name} onChange={v => setForm({ ...form, admin_last_name: v })} testid="tenant-admin-lname" />
          </div>
          <Field label="Plan" value={form.plan} onChange={v => setForm({ ...form, plan: v })} testid="tenant-plan" />
        </form>
        <div className="px-6 py-4 flex justify-end gap-2" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <button type="button" className="pg-btn" onClick={onClose}>Cancel</button>
          <button onClick={submit} className="pg-btn pg-btn-primary" disabled={saving} data-testid="tenant-submit">{saving ? "Creating…" : "Create"}</button>
        </div>
      </div>
    </>
  );
}

function CredentialsPanel({ creds, onClose }) {
  const copy = (s) => { navigator.clipboard.writeText(s); toast.success("Copied"); };
  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} />
      <div className="side-panel" data-testid="tenant-credentials-panel">
        <div className="px-6 py-5" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <h2 className="text-xl">Workspace ready · {creds.tenant.name}</h2>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <p className="text-sm text-dim">Share these one-time credentials with the new admin. They should change the password on first login.</p>
          <CredRow label="Email" value={creds.admin.email} onCopy={() => copy(creds.admin.email)} testid="cred-email" />
          <CredRow label="Temporary Password" value={creds.admin.temp_password} onCopy={() => copy(creds.admin.temp_password)} testid="cred-password" />
          <CredRow label="Workspace Slug" value={creds.tenant.slug} onCopy={() => copy(creds.tenant.slug)} testid="cred-slug" />
        </div>
        <div className="px-6 py-4 flex justify-end" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <button onClick={onClose} className="pg-btn pg-btn-primary">Done</button>
        </div>
      </div>
    </>
  );
}

function CredRow({ label, value, onCopy, testid }) {
  return (
    <div data-testid={testid}>
      <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">{label}</label>
      <div className="flex gap-2">
        <input className="pg-input mono" value={value} readOnly />
        <button onClick={onCopy} className="pg-btn"><Copy size={14} /></button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, required, type = "text", hint }) {
  return (
    <div>
      <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">{label}{required && <span style={{ color: "var(--pg-red)" }}> *</span>}</label>
      <input className="pg-input" type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid} />
      {hint && <div className="mono text-[11px] text-mute mt-1">{hint}</div>}
    </div>
  );
}

function BrandingPanel({ onClose }) {
  const [form, setForm] = useState({
    heading_line_1: "", heading_line_2: "", tagline: "", footer_text: "", logo_data_url: null,
    head_html: "", body_start_html: "", body_end_html: "",
    dashboard_welcome_line_1: "", dashboard_welcome_line_2: "", dashboard_welcome_line_3: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const confirm = useConfirm();

  useEffect(() => {
    api.get("/platform/branding")
      .then((r) => {
        setForm({
          heading_line_1: r.data.heading_line_1 || "",
          heading_line_2: r.data.heading_line_2 || "",
          tagline: r.data.tagline || "",
          footer_text: r.data.footer_text || "",
          logo_data_url: r.data.logo_data_url || null,
          head_html: r.data.head_html || "",
          body_start_html: r.data.body_start_html || "",
          body_end_html: r.data.body_end_html || "",
          dashboard_welcome_line_1: r.data.dashboard_welcome_line_1 || "",
          dashboard_welcome_line_2: r.data.dashboard_welcome_line_2 || "",
          dashboard_welcome_line_3: r.data.dashboard_welcome_line_3 || "",
        });
      })
      .catch(() => toast.error("Failed to load branding"))
      .finally(() => setLoading(false));
  }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 600 * 1024) {
      toast.error("Image too large — keep it under 600KB.");
      e.target.value = "";
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("File must be an image.");
      e.target.value = "";
      return;
    }
    setUploadingLogo(true);
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, logo_data_url: reader.result }));
      setUploadingLogo(false);
    };
    reader.onerror = () => { toast.error("Couldn't read the image."); setUploadingLogo(false); };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => setForm((f) => ({ ...f, logo_data_url: "" }));

  const resetToDefaults = async () => {
    const ok = await confirm({
      title: "Reset branding?",
      message: "This will reset headings, tagline, footer and logo to Pragma defaults. Script snippets are kept.",
      confirmLabel: "Reset",
    });
    if (!ok) return;
    setForm((f) => ({
      ...f,
      heading_line_1: "A practical CRM",
      heading_line_2: "without the bloat.",
      tagline: "No unnecessary features, no steep learning curve. A CRM built the way it should be.",
      footer_text: "v1.0 · PRACTICAL / CLEAN / NO BLOAT",
      logo_data_url: "",
      dashboard_welcome_line_1: "{greeting} {first_name} 👋",
      dashboard_welcome_line_2: "It's time to make your move…",
      dashboard_welcome_line_3: "what would you like to begin with?",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Send logo_data_url as empty string to clear; null to leave untouched
      const payload = {
        heading_line_1: form.heading_line_1,
        heading_line_2: form.heading_line_2,
        tagline: form.tagline,
        footer_text: form.footer_text,
        logo_data_url: form.logo_data_url === null ? undefined : form.logo_data_url,
        head_html: form.head_html,
        body_start_html: form.body_start_html,
        body_end_html: form.body_end_html,
        dashboard_welcome_line_1: form.dashboard_welcome_line_1,
        dashboard_welcome_line_2: form.dashboard_welcome_line_2,
        dashboard_welcome_line_3: form.dashboard_welcome_line_3,
      };
      await api.put("/superadmin/branding", payload);
      invalidateBrandingCache();
      toast.success("Branding updated. Refresh any open Pragma tab to see the changes.");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
    setSaving(false);
  };

  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} />
      <div className="side-panel" data-testid="branding-panel" style={{ width: "min(520px, 92vw)" }}>
        <div className="px-6 py-5 flex items-center justify-between" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <div>
            <h2 className="text-xl">Branding</h2>
            <div className="mono text-[11px] text-mute mt-1">Login & setup pages on every device</div>
          </div>
          <button onClick={resetToDefaults} className="pg-btn pg-btn-ghost" data-testid="branding-reset" title="Reset to defaults">
            <RotateCcw size={14} />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-mute mono text-xs">loading…</div>
        ) : (
          <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            <div>
              <label className="block mono text-[11px] text-mute mb-2 uppercase tracking-wider">Logo</label>
              <div className="flex items-center gap-4">
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: 92, height: 92,
                    background: "var(--pg-surface-2)",
                    border: "1px solid var(--pg-border)",
                    borderRadius: "var(--r-md)",
                    overflow: "hidden",
                  }}
                  data-testid="branding-logo-preview"
                >
                  {form.logo_data_url ? (
                    <img src={form.logo_data_url} alt="logo" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                  ) : (
                    <ImageIcon size={28} className="text-mute" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label
                    className="pg-btn"
                    style={{ cursor: uploadingLogo ? "not-allowed" : "pointer" }}
                    data-testid="branding-upload-btn"
                  >
                    <Upload size={14} />
                    {uploadingLogo ? "Reading…" : (form.logo_data_url ? "Replace" : "Upload")}
                    <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploadingLogo} />
                  </label>
                  {form.logo_data_url && (
                    <button type="button" className="pg-btn pg-btn-danger" onClick={removeLogo} data-testid="branding-remove-logo">
                      Remove
                    </button>
                  )}
                </div>
              </div>
              <div className="mono text-[10px] text-mute mt-2">PNG/SVG/JPG · max 600KB · shown on login pages above the heading</div>
            </div>

            <Lbl label="Heading — line 1">
              <input
                className="pg-input"
                value={form.heading_line_1}
                onChange={(e) => setForm({ ...form, heading_line_1: e.target.value })}
                maxLength={120}
                data-testid="branding-h1"
              />
            </Lbl>
            <Lbl label="Heading — line 2 (accent color)">
              <input
                className="pg-input"
                value={form.heading_line_2}
                onChange={(e) => setForm({ ...form, heading_line_2: e.target.value })}
                maxLength={120}
                data-testid="branding-h2"
              />
            </Lbl>
            <Lbl label="Tagline">
              <textarea
                className="pg-input"
                rows={3}
                value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                maxLength={400}
                data-testid="branding-tagline"
              />
              <div className="mono text-[10px] text-mute mt-1">{form.tagline.length}/400</div>
            </Lbl>
            <Lbl label="Footer text">
              <input
                className="pg-input"
                value={form.footer_text}
                onChange={(e) => setForm({ ...form, footer_text: e.target.value })}
                maxLength={120}
                data-testid="branding-footer"
              />
            </Lbl>

            {/* Dashboard welcome copy */}
            <div style={{ borderTop: "1px solid var(--pg-border)", paddingTop: 18, marginTop: 6 }}>
              <div className="mono text-[11px] text-mute mb-1 uppercase tracking-wider">Dashboard Welcome Copy</div>
              <div className="text-[12px] text-mute" style={{ marginBottom: 14, lineHeight: 1.55 }}>
                Shown at the top of every user's dashboard across all tenants. Use{" "}
                <code className="mono" style={{ background: "var(--pg-surface-2)", padding: "1px 4px", borderRadius: 3 }}>{"{first_name}"}</code>{" "}
                and{" "}
                <code className="mono" style={{ background: "var(--pg-surface-2)", padding: "1px 4px", borderRadius: 3 }}>{"{greeting}"}</code>{" "}
                to personalise (e.g. "Good morning Alex 👋"). Leave a line empty to hide it.
              </div>
              <Lbl label="Line 1 — headline">
                <input
                  className="pg-input"
                  value={form.dashboard_welcome_line_1}
                  onChange={(e) => setForm({ ...form, dashboard_welcome_line_1: e.target.value })}
                  maxLength={200}
                  placeholder="{greeting} {first_name} 👋"
                  data-testid="branding-dash-line-1"
                />
              </Lbl>
              <div style={{ marginTop: 12 }}>
                <Lbl label="Line 2 — subheading">
                  <input
                    className="pg-input"
                    value={form.dashboard_welcome_line_2}
                    onChange={(e) => setForm({ ...form, dashboard_welcome_line_2: e.target.value })}
                    maxLength={200}
                    placeholder="It's time to make your move…"
                    data-testid="branding-dash-line-2"
                  />
                </Lbl>
              </div>
              <div style={{ marginTop: 12 }}>
                <Lbl label="Line 3 — call to action (bold)">
                  <input
                    className="pg-input"
                    value={form.dashboard_welcome_line_3}
                    onChange={(e) => setForm({ ...form, dashboard_welcome_line_3: e.target.value })}
                    maxLength={200}
                    placeholder="what would you like to begin with?"
                    data-testid="branding-dash-line-3"
                  />
                </Lbl>
              </div>
            </div>

            {/* Tracking & marketing scripts */}
            <div style={{ borderTop: "1px solid var(--pg-border)", paddingTop: 18, marginTop: 6 }}>
              <div className="mono text-[11px] text-mute mb-1 uppercase tracking-wider">Tracking &amp; Marketing Scripts</div>
              <div className="text-[12px] text-mute" style={{ marginBottom: 14, lineHeight: 1.55 }}>
                Paste code from Google Tag Manager, Intercom, Crisp, Hotjar, Meta Pixel, etc. Snippets are injected on <strong>every</strong> Pragma page for <strong>every</strong> tenant.
                Max 20&nbsp;KB per field.
              </div>
              <Lbl label="Head — <head>">
                <textarea
                  className="pg-input mono text-[12px]"
                  rows={5}
                  value={form.head_html}
                  onChange={(e) => setForm({ ...form, head_html: e.target.value })}
                  placeholder={`<!-- Google Tag Manager -->\n<script>(function(w,d,s,l,i){...})(window,document,'script','dataLayer','GTM-XXXXXX');</script>`}
                  data-testid="branding-head-html"
                  spellCheck={false}
                />
                <div className="mono text-[10px] text-mute mt-1">GTM script, meta tags, favicons, analytics loaders.</div>
              </Lbl>
              <div style={{ marginTop: 12 }}>
                <Lbl label="Body Start — right after <body>">
                  <textarea
                    className="pg-input mono text-[12px]"
                    rows={4}
                    value={form.body_start_html}
                    onChange={(e) => setForm({ ...form, body_start_html: e.target.value })}
                    placeholder={`<!-- Google Tag Manager (noscript) -->\n<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-XXXXXX" height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>`}
                    data-testid="branding-body-start-html"
                    spellCheck={false}
                  />
                  <div className="mono text-[10px] text-mute mt-1">GTM &lt;noscript&gt; iframe, top-of-page banners.</div>
                </Lbl>
              </div>
              <div style={{ marginTop: 12 }}>
                <Lbl label="Body End — before </body>">
                  <textarea
                    className="pg-input mono text-[12px]"
                    rows={5}
                    value={form.body_end_html}
                    onChange={(e) => setForm({ ...form, body_end_html: e.target.value })}
                    placeholder={`<!-- Intercom -->\n<script>window.intercomSettings = { app_id: 'YOUR_APP_ID' };</script>\n<script>(function(){var w=window;var ic=w.Intercom;...})();</script>`}
                    data-testid="branding-body-end-html"
                    spellCheck={false}
                  />
                  <div className="mono text-[10px] text-mute mt-1">Chat widgets, session recording, footer analytics.</div>
                </Lbl>
              </div>
            </div>

            {/* Live preview */}
            <div>
              <div className="mono text-[11px] text-mute mb-2 uppercase tracking-wider">Preview</div>
              <div className="pg-card p-5" style={{ background: "var(--pg-surface-2)" }}>
                {form.logo_data_url && (
                  <img src={form.logo_data_url} alt="" style={{ maxHeight: 36, maxWidth: 160, marginBottom: 12, objectFit: "contain" }} />
                )}
                <div className="text-2xl" style={{ fontWeight: 700, lineHeight: 1.1, color: "var(--pg-text)" }}>
                  {form.heading_line_1 || "Heading"}<br />
                  <span style={{ color: "var(--pg-accent)" }}>{form.heading_line_2 || "second line"}</span>
                </div>
                <div className="text-sm text-dim mt-3" style={{ lineHeight: 1.5 }}>{form.tagline || "Tagline"}</div>
                <div className="mono text-[10px] text-mute mt-4" style={{ letterSpacing: "0.08em" }}>{form.footer_text}</div>
              </div>
            </div>
          </form>
        )}

        <div className="px-6 py-4 flex justify-end gap-2" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <button type="button" className="pg-btn" onClick={onClose}>Cancel</button>
          <button onClick={submit} className="pg-btn pg-btn-primary" disabled={saving || loading} data-testid="branding-save">
            {saving ? "Saving…" : "Save Branding"}
          </button>
        </div>
      </div>
    </>
  );
}

function Lbl({ label, children }) {
  return (
    <div>
      <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════
// Promo Codes Panel
// ═══════════════════════════════════════════════════════════════════════
function PromoCodesPanel({ onClose }) {
  const confirm = useConfirm();
  const [codes, setCodes] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/superadmin/promo-codes");
      setCodes(r.data);
    } catch (e) { toast.error("Failed to load promo codes"); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!await confirm({ title: "Delete this promo code?", message: "Existing tenants keep their attribution — but new signups can no longer use this code.", destructive: true, confirmLabel: "Delete" })) return;
    try { await api.delete(`/superadmin/promo-codes/${id}`); await load(); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  };

  const toggleActive = async (c) => {
    try { await api.patch(`/superadmin/promo-codes/${c.id}`, { active: !c.active }); await load(); }
    catch { toast.error("Failed"); }
  };

  const shareUrl = (code) => `${window.location.origin}/signup?promo=${encodeURIComponent(code)}`;
  const copy = (code) => {
    const url = shareUrl(code);
    navigator.clipboard.writeText(url).then(() => {
      setCopied(code);
      toast.success("Affiliate link copied");
      setTimeout(() => setCopied(null), 1500);
    });
  };

  const fmt$ = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} />
      <div className="side-panel" data-variant="center" data-testid="promo-panel"
           style={{ maxWidth: 1080 }}>
        <div className="px-6 py-5 flex justify-between items-start"
             style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <div>
            <h2 className="text-xl">Promo Codes</h2>
            <div className="mono text-[11px] text-mute mt-1">
              Extended-trial affiliate codes · share <code>?promo=CODE</code> links with partners
            </div>
          </div>
          <div className="flex gap-2">
            <button className="pg-btn pg-btn-primary" onClick={() => setShowNew(true)}
                    data-testid="promo-new-btn">
              <Plus size={14} /> New code
            </button>
            <button onClick={onClose} className="pg-btn-icon"><X size={16} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {codes.length === 0 ? (
            <div className="text-mute text-sm py-10 text-center">
              No promo codes yet. Click <b>New code</b> to create your first affiliate campaign.
            </div>
          ) : (
            <table className="pg-table" data-testid="promo-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Partner</th>
                  <th className="text-right">Trial</th>
                  <th className="text-right">Uses</th>
                  <th className="text-right">Active subs</th>
                  <th className="text-right">MRR</th>
                  <th className="text-right">Revenue</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {codes.map(c => (
                  <tr key={c.id} data-testid={`promo-row-${c.code}`}>
                    <td>
                      <div style={{ fontFamily: "JetBrains Mono, monospace", fontWeight: 700,
                                    fontSize: 13, color: "var(--pg-text)" }}>
                        {c.code}
                      </div>
                      {c.description && (
                        <div className="mono text-[10px] text-mute mt-0.5">{c.description}</div>
                      )}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13, color: "var(--pg-text)" }}>{c.partner_name}</div>
                      {c.partner_contact && (
                        <div className="mono text-[10px] text-mute">{c.partner_contact}</div>
                      )}
                    </td>
                    <td className="text-right" style={{ fontFeatureSettings: '"tnum" 1', fontWeight: 600 }}>
                      {c.trial_days}d
                    </td>
                    <td className="text-right" style={{ fontFeatureSettings: '"tnum" 1' }}>
                      {c.used_count || 0}
                      {c.max_uses ? <span className="text-mute"> / {c.max_uses}</span> : ""}
                    </td>
                    <td className="text-right" style={{ fontFeatureSettings: '"tnum" 1' }}>{c.active_subscriptions || 0}</td>
                    <td className="text-right" style={{ fontFeatureSettings: '"tnum" 1', color: "var(--pg-accent-deep)" }}>{fmt$(c.mrr_cents)}</td>
                    <td className="text-right" style={{ fontFeatureSettings: '"tnum" 1', fontWeight: 700 }}>{fmt$(c.revenue_total_cents)}</td>
                    <td>
                      <span className={`pg-badge ${c.active ? "pg-badge-green" : "pg-badge-muted"}`}>
                        {c.active ? "Active" : "Paused"}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-1 justify-end">
                        <button className="pg-btn-icon"
                                onClick={() => copy(c.code)}
                                data-testid={`promo-copy-${c.code}`}
                                title="Copy affiliate URL">
                          {copied === c.code ? <span style={{ color: "#00A66E", fontSize: 11 }}>✓</span> : <LinkIcon size={13} />}
                        </button>
                        <button className="pg-btn-icon"
                                onClick={() => toggleActive(c)}
                                title={c.active ? "Pause" : "Activate"}>
                          {c.active ? <Pause size={13} /> : <Play size={13} />}
                        </button>
                        <button className="pg-btn-icon" onClick={() => del(c.id)}
                                data-testid={`promo-delete-${c.code}`}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showNew && <PromoCodeForm onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </>
  );
}

function PromoCodeForm({ onClose, onSaved }) {
  const [form, setForm] = useState({
    code: "", partner_name: "", partner_contact: "", description: "",
    trial_days: 30, active: true, expires_at: "", max_uses: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        partner_name: form.partner_name.trim(),
        partner_contact: form.partner_contact.trim(),
        description: form.description.trim(),
        trial_days: Number(form.trial_days),
        active: form.active,
      };
      if (form.expires_at) payload.expires_at = new Date(form.expires_at).toISOString();
      if (form.max_uses) payload.max_uses = Number(form.max_uses);
      await api.post("/superadmin/promo-codes", payload);
      toast.success(`Promo ${payload.code} created`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} style={{ zIndex: 60 }} />
      <div className="side-panel" data-variant="center" data-testid="promo-form"
           style={{ maxWidth: 520, zIndex: 61 }}>
        <div className="px-6 py-5" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <h2 className="text-xl">New Promo Code</h2>
          <div className="mono text-[11px] text-mute mt-1">
            The <b>partner name</b> is how you'll credit the affiliate later.
          </div>
        </div>
        <form onSubmit={save} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <Lbl label="Code">
            <input className="pg-input" value={form.code}
                   onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                   placeholder="e.g. CREATORX30" maxLength={20} required
                   style={{ textTransform: "uppercase", fontFamily: "JetBrains Mono, monospace" }}
                   data-testid="promo-form-code" />
          </Lbl>
          <div className="grid grid-cols-2 gap-3">
            <Lbl label="Partner name">
              <input className="pg-input" value={form.partner_name}
                     onChange={(e) => setForm({ ...form, partner_name: e.target.value })}
                     placeholder="e.g. Alex Chen" required
                     data-testid="promo-form-partner" />
            </Lbl>
            <Lbl label="Partner contact (optional)">
              <input className="pg-input" value={form.partner_contact}
                     onChange={(e) => setForm({ ...form, partner_contact: e.target.value })}
                     placeholder="alex@influencer.co" />
            </Lbl>
          </div>
          <Lbl label="Description (optional)">
            <input className="pg-input" value={form.description}
                   onChange={(e) => setForm({ ...form, description: e.target.value })}
                   placeholder="LinkedIn campaign · Feb 2026" />
          </Lbl>
          <div className="grid grid-cols-2 gap-3">
            <Lbl label="Trial days (1-90)">
              <input className="pg-input" type="number" min="1" max="90"
                     value={form.trial_days}
                     onChange={(e) => setForm({ ...form, trial_days: e.target.value })}
                     required data-testid="promo-form-days" />
            </Lbl>
            <Lbl label="Max uses (blank = unlimited)">
              <input className="pg-input" type="number" min="1"
                     value={form.max_uses}
                     onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                     placeholder="unlimited" />
            </Lbl>
          </div>
          <Lbl label="Expires at (optional)">
            <input className="pg-input" type="date"
                   value={form.expires_at}
                   onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
          </Lbl>
          <label className="flex items-center gap-2 text-sm" style={{ color: "var(--pg-text)" }}>
            <input type="checkbox" checked={form.active}
                   onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Active immediately
          </label>
        </form>
        <div className="px-6 py-4 flex justify-end gap-2"
             style={{ borderTop: "1px solid var(--pg-border)" }}>
          <button type="button" className="pg-btn" onClick={onClose}>Cancel</button>
          <button className="pg-btn pg-btn-primary" onClick={save} disabled={saving}
                  data-testid="promo-form-save">
            {saving ? "Creating…" : "Create promo"}
          </button>
        </div>
      </div>
    </>
  );
}
